import type { CSSProperties, HTMLAttributes, ReactNode } from "react";

/**
 * TheCore Studio — StatTile.
 * Compact tinted metric tile — streaks, classes taken, minutes.
 * The value is a counter, so it lives in the mono face (Spline Sans Mono),
 * not the serif — measurement, not headline.
 */
export type StatTileTone = "brand" | "sage" | "honey" | "slate" | "neutral";

export interface StatTileProps extends Omit<HTMLAttributes<HTMLDivElement>, "style"> {
  value: ReactNode;
  label: string;
  icon?: ReactNode;
  /** @default 'brand' */
  tone?: StatTileTone;
  style?: CSSProperties;
}

const TONES: Record<StatTileTone, { bg: string; fg: string }> = {
  brand: { bg: "var(--brand-soft)", fg: "var(--brand-press)" },
  sage: { bg: "var(--sage-100)", fg: "var(--sage-700)" },
  honey: { bg: "var(--honey-100)", fg: "var(--honey-700)" },
  slate: { bg: "var(--slate-100)", fg: "var(--slate-700)" },
  neutral: { bg: "var(--surface-muted)", fg: "var(--text-strong)" },
};

export function StatTile({ value, label, icon = null, tone = "brand", style = {}, ...rest }: StatTileProps) {
  const t = TONES[tone] ?? TONES.brand;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "var(--space-4)",
        borderRadius: "var(--radius-md)",
        background: t.bg,
        minWidth: 96,
        ...style,
      }}
      {...rest}
    >
      {icon && (
        <span style={{ color: t.fg, display: "inline-flex" }}>{icon}</span>
      )}
      <span
        style={{
          font: "var(--fw-bold) var(--fs-2xl)/1 var(--font-mono)",
          letterSpacing: "var(--ls-snug)",
          color: t.fg,
        }}
      >
        {value}
      </span>
      <span
        style={{
          font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
          letterSpacing: "var(--ls-wide)",
          textTransform: "uppercase",
          color: t.fg,
          opacity: 0.8,
        }}
      >
        {label}
      </span>
    </div>
  );
}
